import { isDefined } from '@terros/common'

export const MIN_ZOOM = 0.15
export const MAX_ZOOM = 2.5

export const ZOOM_STEP = 0.1

const CLICK_SLOP_PX = 5

const INTERACTIVE_TAGS = ['button', 'input', 'select', 'textarea', 'a[href]', 'label']

const INTERACTIVE_ROLES = [
  'button',
  'checkbox',
  'combobox',
  'dialog',
  'link',
  'listbox',
  'menu',
  'menuitem',
  'menuitemcheckbox',
  'menuitemradio',
  'option',
  'radio',
  'slider',
  'spinbutton',
  'switch',
  'tab',
  'textbox',
]

const PAN_OPT_OUT = ['[draggable="true"]', '[data-no-pan="true"]', '[contenteditable="true"]']

export const OWNS_GESTURE_SELECTOR = [
  ...INTERACTIVE_TAGS,
  ...INTERACTIVE_ROLES.map((role) => `[role="${role}"]`),
  ...PAN_OPT_OUT,
].join(', ')

export type ViewportPan = { x: number; y: number }

export type ViewportDragState = {
  pointerId: number
  startX: number
  startY: number
  originX: number
  originY: number
  panning: boolean
}

type BoundsElement = { getBoundingClientRect: () => { left: number; top: number } }

export type SizeElement = { offsetWidth: number; offsetHeight: number }

type ClosestTarget = { closest: (selector: string) => Node | null }

export type PointerCaptureTarget = {
  contains: (node: Node | null) => boolean
  setPointerCapture: (pointerId: number) => void
  releasePointerCapture: (pointerId: number) => void
  hasPointerCapture: (pointerId: number) => boolean
}

export type ViewportPointerInput = {
  button: number
  pointerId: number
  clientX: number
  clientY: number
  target: EventTarget | ClosestTarget | null
  currentTarget: PointerCaptureTarget
}

export type ViewportWheelInput = {
  clientX: number
  clientY: number
  deltaX: number
  deltaY: number
  ctrlKey: boolean
  preventDefault: () => void
}

const hasClosest = (target: EventTarget | ClosestTarget | null): target is ClosestTarget =>
  isDefined(target) && 'closest' in target && typeof target.closest === 'function'

const ownsPointerGesture = (target: EventTarget | ClosestTarget | null, viewport: PointerCaptureTarget): boolean => {
  if (!hasClosest(target)) return false
  const owner = target.closest(OWNS_GESTURE_SELECTOR)
  if (!isDefined(owner)) return false
  return viewport.contains(owner)
}

const WHEEL_ZOOM_PRECISION = 3
const STEP_ZOOM_PRECISION = 2

const PAN_PRECISION = 2

export const toSubpixelPan = (value: number): number => Number(value.toFixed(PAN_PRECISION))

export const focusedNodeSelector = (nodeDomId: string): string => `[data-node-id="${CSS.escape(nodeDomId)}"]`

export const clampZoom = (zoom: number, precision: number): number =>
  Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, Number(zoom.toFixed(precision))))

export const MIN_LEGIBLE_FIT_ZOOM = 0.5

const FIT_ZOOM_PRECISION = 2

type FitArgs = { container: SizeElement; content: SizeElement }

export const toFitZoom = ({ container, content }: FitArgs): number | undefined => {
  if (content.offsetWidth === 0 || content.offsetHeight === 0) return
  if (container.offsetWidth === 0 || container.offsetHeight === 0) return

  const widthRatio = container.offsetWidth / content.offsetWidth
  const heightRatio = container.offsetHeight / content.offsetHeight
  const fitZoom = clampZoom(Math.min(1, widthRatio, heightRatio), FIT_ZOOM_PRECISION)

  return Math.max(fitZoom, MIN_LEGIBLE_FIT_ZOOM)
}

type ZoomAnchor = {
  anchorX: number
  anchorY: number
  pan: ViewportPan
  fromZoom: number
  toZoom: number
}

export const panForZoomAtPoint = ({ anchorX, anchorY, pan, fromZoom, toZoom }: ZoomAnchor): ViewportPan => {
  const worldX = (anchorX - pan.x) / fromZoom
  const worldY = (anchorY - pan.y) / fromZoom
  return { x: toSubpixelPan(anchorX - worldX * toZoom), y: toSubpixelPan(anchorY - worldY * toZoom) }
}

type PointerHandlerArgs = {
  dragRef: { current: ViewportDragState | null }
  panRef: { current: ViewportPan }
  setPan: (pan: ViewportPan) => void
  setIsPanning: (isPanning: boolean) => void
  onInteractionStart: () => void
}

type PointerHandlers = {
  onPointerDown: (event: ViewportPointerInput) => void
  onPointerMove: (event: ViewportPointerInput) => void
  onPointerEnd: (event: ViewportPointerInput) => void
}

export const createPointerHandlers = ({
  dragRef,
  panRef,
  setPan,
  setIsPanning,
  onInteractionStart,
}: PointerHandlerArgs): PointerHandlers => ({
  onPointerDown: (event: ViewportPointerInput): void => {
    if (event.button !== 0) return
    if (ownsPointerGesture(event.target, event.currentTarget)) return
    onInteractionStart()
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originX: panRef.current.x,
      originY: panRef.current.y,
      panning: false,
    }
  },
  onPointerMove: (event: ViewportPointerInput): void => {
    const drag = dragRef.current
    if (!drag || drag.pointerId !== event.pointerId) return

    if (!drag.panning) {
      const travelX = Math.abs(event.clientX - drag.startX)
      const travelY = Math.abs(event.clientY - drag.startY)
      if (travelX < CLICK_SLOP_PX && travelY < CLICK_SLOP_PX) return
      drag.panning = true
      setIsPanning(true)
      event.currentTarget.setPointerCapture(event.pointerId)
    }

    setPan({
      x: drag.originX + event.clientX - drag.startX,
      y: drag.originY + event.clientY - drag.startY,
    })
  },
  onPointerEnd: (event: ViewportPointerInput): void => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
    const drag = dragRef.current
    if (!drag || drag.pointerId !== event.pointerId) return
    dragRef.current = null
    if (!drag.panning) return
    setIsPanning(false)
  },
})

type WheelHandlerArgs = {
  containerRef: { current: BoundsElement | null }
  zoomRef: { current: number }
  panRef: { current: ViewportPan }
  setZoom: (zoom: number) => void
  setPan: (pan: ViewportPan) => void
  onInteractionStart: () => void
}

export const createGreedyWheelHandler = ({
  containerRef,
  zoomRef,
  panRef,
  setZoom,
  setPan,
  onInteractionStart,
}: WheelHandlerArgs): ((event: ViewportWheelInput) => void) => {
  return (event: ViewportWheelInput): void => {
    event.preventDefault()
    const container = containerRef.current
    if (!container) return
    onInteractionStart()

    if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) {
      setPan({ x: toSubpixelPan(panRef.current.x - event.deltaX), y: panRef.current.y })
      return
    }

    if (event.deltaY === 0) return

    const bounds = container.getBoundingClientRect()
    const anchorX = event.clientX - bounds.left
    const anchorY = event.clientY - bounds.top
    const sensitivity = event.ctrlKey ? 0.005 : 0.004
    const fromZoom = zoomRef.current
    const toZoom = clampZoom(fromZoom + -event.deltaY * sensitivity, WHEEL_ZOOM_PRECISION)
    if (toZoom === fromZoom) return

    setZoom(toZoom)
    setPan(panForZoomAtPoint({ anchorX, anchorY, pan: panRef.current, fromZoom, toZoom }))
  }
}

export type BoundsRect = { left: number; top: number; right: number; bottom: number }

const axisShiftToReveal = (
  nodeStart: number,
  nodeEnd: number,
  containerStart: number,
  containerEnd: number,
  margin: number
): number => {
  const insetStart = containerStart + margin
  const insetEnd = containerEnd - margin
  if (nodeStart < insetStart) return insetStart - nodeStart
  if (nodeEnd > insetEnd) return Math.max(insetEnd - nodeEnd, insetStart - nodeStart)
  return 0
}

type PanShiftArgs = { nodeBounds: BoundsRect; containerBounds: BoundsRect; margin: number }

export const panShiftToReveal = ({ nodeBounds, containerBounds, margin }: PanShiftArgs): ViewportPan | undefined => {
  const x = axisShiftToReveal(nodeBounds.left, nodeBounds.right, containerBounds.left, containerBounds.right, margin)
  const y = axisShiftToReveal(nodeBounds.top, nodeBounds.bottom, containerBounds.top, containerBounds.bottom, margin)
  if (x === 0 && y === 0) return undefined
  return { x, y }
}

type StepZoomArgs = {
  container: SizeElement
  zoom: number
  pan: ViewportPan
  delta: number
}

type StepZoomResult = { zoom: number; pan: ViewportPan }

export const stepZoomAtCenter = ({ container, zoom, pan, delta }: StepZoomArgs): StepZoomResult => {
  const toZoom = clampZoom(zoom + delta, STEP_ZOOM_PRECISION)
  return {
    zoom: toZoom,
    pan: panForZoomAtPoint({
      anchorX: container.offsetWidth / 2,
      anchorY: container.offsetHeight / 2,
      pan,
      fromZoom: zoom,
      toZoom,
    }),
  }
}
